import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface UseUploadFilesParams {
  maxBytes: number;
  maxFiles: number;
  maxTotalBytes: number;
  acceptedFileTypes: readonly string[];
  disabled: boolean;
  onFilesChosen: (files: File[]) => void;
}

interface UseUploadFilesResult {
  handleFiles: (fileList: FileList | null) => void;
  localError: string | null;
  clearError: () => void;
  maxMb: number;
  maxTotalMb: number;
}

/**
 * Shared client-side file validation for both ``UploadDropzone`` (hero
 * variant on Landing) and ``CompactUploadDropzone`` (cart "add another
 * part" row). Validation is purely UX — the server re-validates every
 * rule — but surfacing errors instantly beats a round-trip.
 *
 * Any single violation aborts the whole drop: simpler UX than partial
 * acceptance at client gate time.
 */
export function useUploadFiles({
  maxBytes,
  maxFiles,
  maxTotalBytes,
  acceptedFileTypes,
  disabled,
  onFilesChosen,
}: UseUploadFilesParams): UseUploadFilesResult {
  const { t } = useTranslation("quote");
  const [localError, setLocalError] = useState<string | null>(null);

  const maxMb = Math.floor(maxBytes / (1024 * 1024));
  const maxTotalMb = Math.floor(maxTotalBytes / (1024 * 1024));

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (disabled) return;
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);

      if (files.length > maxFiles) {
        setLocalError(
          t("upload.errorTooManyFiles", { max: maxFiles, got: files.length }),
        );
        return;
      }

      const acceptLower = acceptedFileTypes.map((e) => e.toLowerCase());
      for (const f of files) {
        const lower = f.name.toLowerCase();
        // No declared types = accept any extension (matches the BE, which only
        // enforces extensions when a slot lists them). Without this an optional
        // multi-file slot with empty accepted_file_types would reject every file.
        if (acceptLower.length > 0 && !acceptLower.some((ext) => lower.endsWith(ext))) {
          // KAN-995 — the message has to be actionable, not just a refusal.
          // A shop's router may route a neighbouring CAD format to this
          // process (``inference_file_types``) that its analyzer cannot
          // parse, so "wrong extension" is most often "you exported the
          // wrong flavour" — name what we do read and which one to pick.
          // Both lists come from the process definition; nothing here
          // knows a format by name.
          setLocalError(
            t("upload.errorWrongExt", {
              name: f.name,
              accepted: acceptedFileTypes.join(", "),
              primary: acceptedFileTypes[0],
            }),
          );
          return;
        }
        if (f.size === 0) {
          setLocalError(
            files.length === 1
              ? t("upload.errorEmpty")
              : t("upload.errorEmptyFile", { name: f.name }),
          );
          return;
        }
        if (f.size > maxBytes) {
          setLocalError(
            files.length === 1
              ? t("upload.errorTooLarge", { maxMb })
              : t("upload.errorFileTooLarge", {
                  name: f.name,
                  maxMb,
                }),
          );
          return;
        }
      }

      if (files.length > 1) {
        const total = files.reduce((sum, f) => sum + f.size, 0);
        if (total > maxTotalBytes) {
          setLocalError(
            t("upload.errorBatchTooLarge", { maxMb: maxTotalMb }),
          );
          return;
        }
      }

      setLocalError(null);
      onFilesChosen(files);
    },
    [
      acceptedFileTypes,
      disabled,
      maxBytes,
      maxFiles,
      maxMb,
      maxTotalBytes,
      maxTotalMb,
      onFilesChosen,
      t,
    ],
  );

  const clearError = useCallback(() => setLocalError(null), []);

  return { handleFiles, localError, clearError, maxMb, maxTotalMb };
}
